import { useState } from 'react';
import type { AltYDevEnv } from '../types/global';

// Left-area panel shown when an Online Dev MVP chat has a live dev fork. The
// iframe is sized to fill below a thin toolbar carrying the X close button and
// the Ship It button — by design, the iframe sits BELOW those controls so they
// never cover the app underneath.
export function OnlineDevPanel({
  devEnv,
  runId,
  onClose,
  onShip,
  busy,
  chatTitle,
}: {
  devEnv: AltYDevEnv;
  runId: number | null;
  onClose: () => void;
  onShip: (prompt: string) => void;
  busy: boolean;
  chatTitle?: string;
}) {
  const [activeTab, setActiveTab] = useState<'environment' | 'proof'>('environment');
  const proof = devEnv.proof;
  const screenshots = Array.isArray(proof?.screenshots) ? proof.screenshots : [];
  const confidence = typeof proof?.confidence === 'number' ? Math.round(proof.confidence) : null;

  const handleShip = () => {
    if (runId == null) return;
    if (!window.confirm(
      `Push this dev fork's changes to the testing branch via the AI developer agent?\n\nDev: ${devEnv.name}\nURL: ${devEnv.url}`,
    )) return;

    const prompt = `Please commit and ship the changes from my dev instance to the upstream testing branch.
- First, run 'git status' and 'git diff' to review all changes.
- Stage all changed files.
- Commit them with the message: 'Ship: ${chatTitle || `online-dev-mvp #${runId}`}' (From Alt-Y Online Dev MVP chat #${runId}). Configure git user.name="Alt-Y" and user.email="alty@idsys.ee" if needed.
- Fetch and merge origin/testing (if there are any merge conflicts, resolve them, stage the resolved files, commit the merge).
- Push your committed HEAD to testing (origin HEAD:testing).
- Confirm once successfully shipped.`;

    onShip(prompt);
  };

  const shipDisabled = busy || runId == null;
  const shipLabel = busy ? 'Agent active…' : 'Ship it';

  return (
    <section className="online-dev-panel">
      <header className="online-dev-toolbar">
        <div className="online-dev-meta">
          <span className="online-dev-name">
            <i className="fas fa-globe" aria-hidden="true" /> {devEnv.name || 'dev env'}
          </span>
          <a className="online-dev-url" href={devEnv.url} target="_blank" rel="noreferrer">
            {devEnv.url}
          </a>
          {busy ? (
            <span className="online-dev-status">Agent is active…</span>
          ) : null}
        </div>
        <div className="online-dev-actions">
          <div className="online-dev-tabs" role="tablist" aria-label="Dev environment views">
            <button
              type="button"
              className={activeTab === 'environment' ? 'online-dev-tab active' : 'online-dev-tab'}
              onClick={() => setActiveTab('environment')}
              role="tab"
              aria-selected={activeTab === 'environment'}
              title="Environment"
            >
              <i className="fas fa-window-maximize" aria-hidden="true" />
            </button>
            <button
              type="button"
              className={activeTab === 'proof' ? 'online-dev-tab active' : 'online-dev-tab'}
              onClick={() => setActiveTab('proof')}
              role="tab"
              aria-selected={activeTab === 'proof'}
              title="Proof screenshots"
              disabled={!proof && screenshots.length === 0}
            >
              <i className="fas fa-images" aria-hidden="true" />
              {confidence != null ? <span>{confidence}%</span> : null}
            </button>
          </div>
          <button
            type="button"
            className="online-dev-ship"
            onClick={handleShip}
            disabled={shipDisabled}
            title="Commit + push this dev fork to the testing branch via the AI dev agent"
          >
            <i className="fas fa-rocket" aria-hidden="true" /> {shipLabel}
          </button>
          <button
            type="button"
            className="online-dev-close"
            onClick={onClose}
            title="Close — keeps the dev env running"
            aria-label="Close dev env panel"
          >
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>
      </header>
      {activeTab === 'proof' ? (
        <div className="online-dev-proof">
          <div className="online-dev-proof-summary">
            <span className="online-dev-proof-title">
              <i className="fas fa-vial" aria-hidden="true" /> Playwright proof
            </span>
            {confidence != null ? <span className="online-dev-proof-score">{confidence}% confidence</span> : null}
            {proof?.summary ? <p>{proof.summary}</p> : null}
          </div>
          {screenshots.length ? (
            <div className="online-dev-proof-grid">
              {screenshots.map((shot, index) => (
                <a key={`${shot.url}-${index}`} href={shot.url} target="_blank" rel="noreferrer" className="online-dev-proof-shot">
                  <img src={shot.url} alt={shot.caption || `Proof screenshot ${index + 1}`} loading="lazy" />
                  {shot.caption ? <span>{shot.caption}</span> : null}
                </a>
              ))}
            </div>
          ) : (
            <div className="online-dev-proof-empty">No proof screenshots yet.</div>
          )}
        </div>
      ) : (
        <iframe
          key={devEnv.url}
          className="online-dev-frame"
          src={devEnv.url}
          title={`Online Dev MVP — ${devEnv.name || 'dev env'}`}
        />
      )}
    </section>
  );
}
